import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, UserPlus, MessageSquare, Newspaper, Search, Filter, ChevronRight, MapPin, Trophy, Star, ArrowLeft, Home, LogIn, LogOut, Send, Heart, MessageCircle, Settings, Lock, Globe, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { playerAPI, communityTeamAPI, feedAPI, messageAPI } from '../../services/community';
import authService from '../../services/auth';
import CreatePlayerProfile from './CreatePlayerProfile';
import RegisterTeam from './RegisterTeam';
import EditPlayerProfile from './EditPlayerProfile';
import EditTeamProfile from './EditTeamProfile';
import NotificationBell from '../common/NotificationBell';
import AuthModal from '../auth/AuthModal';

const CommunityHub = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('feed');
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [showRegisterTeam, setShowRegisterTeam] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  
  // Auth state
  const [currentUser, setCurrentUser] = useState(location.state?.user || null);
  const [authLoading, setAuthLoading] = useState(!location.state?.user);
  
  // Data states
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  
  // New post state
  const [newPostContent, setNewPostContent] = useState('');
  const [postSubmitting, setPostSubmitting] = useState(false);
  
  // Messaging state
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  
  // Privacy settings state
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [privacyPassword, setPrivacyPassword] = useState('');
  const [privacyUpdating, setPrivacyUpdating] = useState(false);
  
  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Use ref to persist active conversation across re-renders
  const activeConvRef = useRef(null);

  useEffect(() => {
    // Check authentication on mount
    const checkAuth = async () => {
      // If user data was passed from AuthCallback, use it
      if (location.state?.user) {
        setCurrentUser(location.state.user);
        authService.storeUser(location.state.user);
        setAuthLoading(false);
        return;
      }

      // Otherwise, check with backend
      try {
        const user = await authService.getCurrentUser();
        if (user) {
          setCurrentUser(user);
          authService.storeUser(user);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, [location.state]);

  useEffect(() => {
    // Load current player from localStorage
    const storedPlayer = localStorage.getItem('gwins_player');
    if (storedPlayer) {
      setCurrentPlayer(JSON.parse(storedPlayer));
    }
    
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [playersData, teamsData, postsData] = await Promise.all([
        playerAPI.getAll({ limit: 20 }),
        communityTeamAPI.getAll({ limit: 20 }),
        feedAPI.getPosts({ limit: 20 }),
      ]);
      
      setPlayers(playersData.players || []);
      setTeams(teamsData.teams || []);
      setPosts(postsData.posts || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileCreated = (player) => {
    setCurrentPlayer(player);
    setShowCreateProfile(false);
    fetchData();
  };

  const handleTeamCreated = (team) => {
    setShowRegisterTeam(false);
    fetchData();
  };

  const handleLogin = () => {
    setShowAuthModal(true);
  };

  const handleAuthSuccess = async (user) => {
    setCurrentUser(user);
    setShowAuthModal(false);
    // Check if user has a player profile
    try {
      const profiles = await playerAPI.getByUserId(user.user_id);
      if (profiles && profiles.length > 0) {
        const player = profiles[0];
        setCurrentPlayer(player);
        localStorage.setItem('gwins_player', JSON.stringify(player));
      }
    } catch (error) {
      console.error('Error fetching player profile:', error);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    setCurrentUser(null);
    setCurrentPlayer(null);
  };

  const handlePrivacyToggle = async () => {
    if (!currentPlayer || !privacyPassword.trim()) return;
    
    setPrivacyUpdating(true);
    try {
      const newPrivacy = currentPlayer.privacy === 'private' ? 'public' : 'private';
      await playerAPI.updatePrivacy(currentPlayer.player_id, newPrivacy, privacyPassword);
      
      // Update local state
      const updatedPlayer = { ...currentPlayer, privacy: newPrivacy };
      setCurrentPlayer(updatedPlayer);
      localStorage.setItem('gwins_player', JSON.stringify(updatedPlayer));
      
      setShowPrivacyModal(false);
      setPrivacyPassword('');
      alert(`Profile is now ${newPrivacy}`);
    } catch (error) {
      console.error('Error updating privacy:', error);
      alert(error.message || 'Failed to update privacy settings');
    } finally {
      setPrivacyUpdating(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() || !currentPlayer) return;
    
    setPostSubmitting(true);
    try {
      await feedAPI.createPost(currentPlayer.player_id, newPostContent.trim());
      setNewPostContent('');
      fetchData(); // Refresh feed
    } catch (error) {
      console.error('Error creating post:', error);
    } finally {
      setPostSubmitting(false);
    }
  };

  const handleLikePost = async (postId) => {
    if (!currentPlayer) return;
    try {
      await feedAPI.toggleLike(postId, currentPlayer.player_id);
      fetchData(); // Refresh feed
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  // Load conversations when switching to messages tab
  const loadConversations = useCallback(async (skipOverwrite = false) => {
    if (!currentPlayer) return;
    try {
      const data = await messageAPI.getInbox(currentPlayer.player_id);
      setConversations(prev => {
        // If we have a new pending conversation, merge it in
        if (skipOverwrite && prev.length > 0) {
          const existingIds = new Set(data.conversations?.map(c => c.conversation_id) || []);
          const newConvs = prev.filter(c => c.conversation_id?.startsWith('new_') && !existingIds.has(c.conversation_id));
          return [...newConvs, ...(data.conversations || [])];
        }
        return data.conversations || [];
      });
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, [currentPlayer]);
  
  useEffect(() => {
    if (activeTab === 'messages' && currentPlayer) {
      // Skip overwrite if we just started a new conversation
      loadConversations(true);
    }
  }, [activeTab, currentPlayer, loadConversations]);

  const loadConversation = async (otherId) => {
    if (!currentPlayer) return;
    try {
      const data = await messageAPI.getConversation(currentPlayer.player_id, otherId);
      setConversationMessages(data.messages || []);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const handleSendMessage = async () => {
    const currentConv = activeConversation || activeConvRef.current;
    if (!newMessage.trim() || !currentPlayer || !currentConv) return;
    
    setMessageSending(true);
    try {
      await messageAPI.send(
        currentPlayer.player_id,
        currentConv.other_participant?.player_id || currentConv.other_participant?.team_id,
        currentConv.participant_type || 'player',
        newMessage.trim()
      );
      setNewMessage('');
      loadConversation(currentConv.other_participant?.player_id || currentConv.other_participant?.team_id);
      loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setMessageSending(false);
    }
  };

  const startConversationWith = (player) => {
    // Create a new conversation object for starting a chat
    const newConv = {
      conversation_id: `new_${player.player_id}`,
      other_participant: player,
      participant_type: 'player',
      last_message: 'New conversation',
    };
    // Store in ref immediately
    activeConvRef.current = newConv;
    // Set state
    setConversationMessages([]);
    setSelectedPlayer(null);
    setActiveConversation(newConv);
    setActiveTab('messages');
  };

  if (showCreateProfile) {
    return (
      <CreatePlayerProfile
        onBack={() => setShowCreateProfile(false)}
        onSuccess={handleProfileCreated}
      />
    );
  }

  if (showRegisterTeam) {
    return (
      <RegisterTeam
        onBack={() => setShowRegisterTeam(false)}
        onSuccess={handleTeamCreated}
        currentPlayer={currentPlayer}
      />
    );
  }

  if (showEditProfile && currentPlayer) {
    return (
      <EditPlayerProfile
        player={currentPlayer}
        onBack={() => setShowEditProfile(false)}
        onSuccess={(updatedPlayer) => {
          setCurrentPlayer(updatedPlayer);
          setShowEditProfile(false);
        }}
      />
    );
  }

  if (showEditTeam && editingTeam) {
    return (
      <EditTeamProfile
        team={editingTeam}
        onBack={() => {
          setShowEditTeam(false);
          setEditingTeam(null);
        }}
        onSuccess={(updatedTeam) => {
          // Update the team in the teams list
          setTeams(prev => prev.map(t => t.team_id === updatedTeam.team_id ? updatedTeam : t));
          setShowEditTeam(false);
          setEditingTeam(null);
        }}
      />
    );
  }

  const filteredPlayers = players.filter(p => 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.nickname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTeams = teams.filter(t =>
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0a0b0c]">
      {/* Top Navigation Bar */}
      <div className="bg-[#131416] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_3118c9a4-a94d-4f60-9004-669a5548876d/artifacts/9jvjmy2x_GWINS_Symbol_Metallic_2000px.png" 
              alt="GWINS" 
              className="h-8 w-auto"
            />
            {/* Auth Button */}
            {authLoading ? (
              <div className="w-8 h-8 border-2 border-[#00d4aa] border-t-transparent rounded-full animate-spin" />
            ) : currentUser ? (
              <div className="flex items-center gap-3">
                {/* Notification Bell - only show if user has a player profile */}
                {currentPlayer && (
                  <NotificationBell 
                    playerId={currentPlayer.player_id} 
                    onNavigate={(tab) => setActiveTab(tab)}
                  />
                )}
                <div className="flex items-center gap-2">
                  {currentUser.picture && (
                    <img 
                      src={currentUser.picture} 
                      alt={currentUser.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  )}
                  <span className="text-white/80 text-sm hidden sm:block">{currentUser.name}</span>
                </div>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-white/70 hover:text-white hover:bg-white/5"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleLogin}
                className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Login
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-b from-[#131416] to-[#0a0b0c] py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h1 className="text-4xl font-black text-white uppercase">Community Hub</h1>
              <p className="text-white/60 mt-2">Connect with players and teams worldwide</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              {!currentPlayer ? (
                <>
                  <Button
                    onClick={() => setShowCreateProfile(true)}
                    className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Profile
                  </Button>
                  <Button
                    onClick={() => setShowRegisterTeam(true)}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Register Team
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => setShowEditProfile(true)}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5"
                    data-testid="edit-profile-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                  <Button
                    onClick={() => setShowRegisterTeam(true)}
                    className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Register Team
                  </Button>
                  {/* Privacy Toggle */}
                  <Button
                    onClick={() => setShowPrivacyModal(true)}
                    variant="outline"
                    className={`border-white/20 hover:bg-white/5 ${
                      currentPlayer.privacy === 'private' 
                        ? 'text-yellow-400 border-yellow-400/30' 
                        : 'text-white'
                    }`}
                    title={`Profile is ${currentPlayer.privacy || 'public'}`}
                    data-testid="privacy-toggle-btn"
                  >
                    {currentPlayer.privacy === 'private' ? (
                      <><Lock className="h-4 w-4 mr-2" />Private</>
                    ) : (
                      <><Globe className="h-4 w-4 mr-2" />Public</>
                    )}
                  </Button>
                  <Badge className="bg-[#00d4aa]/20 text-[#00d4aa] px-4 py-2">
                    Logged in as: {currentPlayer.name}
                  </Badge>
                </>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="mt-8 relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search players, teams..."
              className="pl-12 bg-[#1a1c1f] border-white/10 text-white h-12"
            />
          </div>

          {/* Tabs */}
          <div className="mt-8 flex gap-2">
            {[
              { id: 'feed', label: 'Feed', icon: Newspaper },
              { id: 'players', label: 'Players', icon: UserPlus },
              { id: 'teams', label: 'Teams', icon: Users },
              { id: 'messages', label: 'Messages', icon: MessageSquare },
            ].map(tab => (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                variant={activeTab === tab.id ? 'default' : 'ghost'}
                className={activeTab === tab.id 
                  ? 'bg-[#00d4aa] text-[#0a0b0c] font-bold' 
                  : 'text-white/60 hover:text-white hover:bg-white/5'
                }
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-[#00d4aa] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Feed Tab */}
            {activeTab === 'feed' && (
              <div className="space-y-6">
                {/* Create Post Section - Only show if user has a player profile */}
                {currentPlayer && (
                  <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] font-bold flex-shrink-0">
                        {currentPlayer.profile_picture ? (
                          <img src={currentPlayer.profile_picture} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          currentPlayer.name?.charAt(0) || '?'
                        )}
                      </div>
                      <div className="flex-1">
                        <textarea
                          value={newPostContent}
                          onChange={(e) => setNewPostContent(e.target.value)}
                          placeholder="Share something with the community..."
                          rows={3}
                          className="w-full bg-[#1a1c1f] border border-white/10 text-white rounded-lg px-4 py-3 resize-none focus:border-[#00d4aa]/50 focus:outline-none"
                        />
                        <div className="flex justify-end mt-3">
                          <Button
                            onClick={handleCreatePost}
                            disabled={!newPostContent.trim() || postSubmitting}
                            className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
                          >
                            {postSubmitting ? (
                              <>
                                <div className="w-4 h-4 border-2 border-[#0a0b0c] border-t-transparent rounded-full animate-spin mr-2" />
                                Posting...
                              </>
                            ) : (
                              <>
                                <Send className="h-4 w-4 mr-2" />
                                Post
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* No Player Profile Message */}
                {!currentPlayer && currentUser && (
                  <div className="bg-[#131416] rounded-xl p-6 border border-[#00d4aa]/30 text-center">
                    <p className="text-white/70 mb-3">Create a player profile to post and interact with the community</p>
                    <Button
                      onClick={() => setShowCreateProfile(true)}
                      className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Create Profile
                    </Button>
                  </div>
                )}
                
                {/* Posts */}
                {posts.length === 0 ? (
                  <div className="text-center py-20">
                    <Newspaper className="h-16 w-16 text-white/20 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">No posts yet</h3>
                    <p className="text-white/60">Be the first to share something!</p>
                  </div>
                ) : (
                  posts.map(post => (
                    <div key={post.post_id} className="bg-[#131416] rounded-xl p-6 border border-white/10">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] font-bold flex-shrink-0 overflow-hidden">
                          {post.author_picture ? (
                            <img src={post.author_picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            post.author_name?.charAt(0) || '?'
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-semibold">{post.author_name}</span>
                            {post.team_name && (
                              <Badge variant="outline" className="border-[#00d4aa]/30 text-[#00d4aa] text-xs">
                                {post.team_name}
                              </Badge>
                            )}
                          </div>
                          <p className="text-white/80 mt-2 whitespace-pre-wrap">{post.content}</p>
                          {post.image_url && (
                            <img src={post.image_url} alt="" className="mt-4 rounded-lg max-h-96 object-cover" />
                          )}
                          <div className="flex items-center gap-6 mt-4">
                            <button 
                              onClick={() => handleLikePost(post.post_id)}
                              className={`flex items-center gap-2 text-sm transition-colors ${
                                post.likes?.includes(currentPlayer?.player_id) 
                                  ? 'text-red-400' 
                                  : 'text-white/50 hover:text-red-400'
                              }`}
                              disabled={!currentPlayer}
                            >
                              <Heart className={`h-4 w-4 ${post.likes?.includes(currentPlayer?.player_id) ? 'fill-current' : ''}`} />
                              {post.like_count || 0} likes
                            </button>
                            <span className="flex items-center gap-2 text-white/50 text-sm">
                              <MessageCircle className="h-4 w-4" />
                              {post.comment_count || 0} comments
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Players Tab */}
            {activeTab === 'players' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPlayers.length === 0 ? (
                  <div className="col-span-full text-center py-20">
                    <UserPlus className="h-16 w-16 text-white/20 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">No players found</h3>
                    <p className="text-white/60">Be the first to create a profile!</p>
                    <Button
                      onClick={() => setShowCreateProfile(true)}
                      className="mt-4 bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c]"
                    >
                      Create Profile
                    </Button>
                  </div>
                ) : (
                  filteredPlayers.map(player => (
                    <div key={player.player_id} className="bg-[#131416] rounded-xl p-6 border border-white/10 hover:border-[#00d4aa]/30 transition-colors">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-full bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-2xl font-bold flex-shrink-0 overflow-hidden">
                          {player.profile_picture ? (
                            <img src={player.profile_picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            player.name?.charAt(0) || '?'
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-bold truncate">{player.name}</h3>
                          {player.nickname && (
                            <p className="text-[#00d4aa] text-sm">&quot;{player.nickname}&quot;</p>
                          )}
                          {player.location && (
                            <p className="text-white/50 text-sm flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {player.location}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {player.experience_level && (
                          <Badge variant="outline" className="border-white/20 text-white/70 text-xs">
                            {player.experience_level}
                          </Badge>
                        )}
                        {player.favorite_position && (
                          <Badge variant="outline" className="border-white/20 text-white/70 text-xs">
                            {player.favorite_position}
                          </Badge>
                        )}
                      </div>
                      {player.bio && (
                        <p className="text-white/50 text-sm mt-3 line-clamp-2">{player.bio}</p>
                      )}
                      {/* Message Button - only show if logged in with profile and not own card */}
                      {currentPlayer && player.player_id !== currentPlayer.player_id && (
                        <Button
                          onClick={() => startConversationWith(player)}
                          variant="outline"
                          className="mt-4 w-full border-[#00d4aa]/30 text-[#00d4aa] hover:bg-[#00d4aa]/10"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Message
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Teams Tab */}
            {activeTab === 'teams' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTeams.length === 0 ? (
                  <div className="col-span-full text-center py-20">
                    <Users className="h-16 w-16 text-white/20 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">No teams found</h3>
                    <p className="text-white/60">Register your team and start recruiting!</p>
                    {currentPlayer && (
                      <Button
                        onClick={() => setShowRegisterTeam(true)}
                        className="mt-4 bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c]"
                      >
                        Register Team
                      </Button>
                    )}
                  </div>
                ) : (
                  filteredTeams.map(team => (
                    <div key={team.team_id} className="bg-[#131416] rounded-xl p-6 border border-white/10 hover:border-[#00d4aa]/30 transition-colors">
                      <div className="flex items-start gap-4">
                        {team.logo_url ? (
                          <img src={team.logo_url} alt={team.name} className="w-16 h-16 rounded-xl object-cover" />
                        ) : (
                          <div className="w-16 h-16 rounded-xl bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-2xl font-bold flex-shrink-0">
                            {team.name?.charAt(0) || '?'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="text-white font-bold truncate">{team.name}</h3>
                            {currentPlayer && team.owner_id === currentPlayer.player_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingTeam(team);
                                  setShowEditTeam(true);
                                }}
                                className="text-[#00d4aa] hover:text-[#33e0bb] hover:bg-[#00d4aa]/10 text-xs px-2"
                              >
                                <Settings className="h-3 w-3 mr-1" />
                                Edit
                              </Button>
                            )}
                          </div>
                          {team.location && (
                            <p className="text-white/50 text-sm flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {team.location}
                            </p>
                          )}
                          <p className="text-white/50 text-sm mt-1">
                            {team.member_count || 1} member{(team.member_count || 1) !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {team.division && (
                          <Badge variant="outline" className="border-white/20 text-white/70 text-xs">
                            {team.division}
                          </Badge>
                        )}
                        {team.looking_for_players && (
                          <Badge className="bg-green-500/20 text-green-400 text-xs">
                            Recruiting
                          </Badge>
                        )}
                        {team.sponsored && (
                          <Badge className="bg-[#00d4aa]/20 text-[#00d4aa] text-xs">
                            <Star className="h-3 w-3 mr-1" />
                            Sponsored
                          </Badge>
                        )}
                      </div>
                      {team.description && (
                        <p className="text-white/50 text-sm mt-3 line-clamp-2">{team.description}</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <div>
                {!currentPlayer ? (
                  <div className="text-center py-20">
                    <MessageSquare className="h-16 w-16 text-white/20 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">Create a profile to message</h3>
                    <p className="text-white/60 mb-4">You need a player profile to send and receive messages</p>
                    <Button
                      onClick={() => setShowCreateProfile(true)}
                      className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c]"
                    >
                      Create Profile
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[500px]">
                    {/* Conversations List */}
                    <div className="bg-[#131416] rounded-xl border border-white/10 overflow-hidden">
                      <div className="p-4 border-b border-white/10">
                        <h3 className="text-white font-bold">Conversations</h3>
                      </div>
                      <div className="divide-y divide-white/5">
                        {conversations.length === 0 ? (
                          <div className="p-4 text-center text-white/50 text-sm">
                            No conversations yet. Message a player from the Players tab!
                          </div>
                        ) : (
                          conversations.map(conv => (
                            <button
                              key={conv.conversation_id}
                              onClick={() => {
                                setActiveConversation(conv);
                                loadConversation(conv.other_participant?.player_id || conv.other_participant?.team_id);
                              }}
                              className={`w-full p-4 text-left hover:bg-white/5 transition-colors ${
                                activeConversation?.conversation_id === conv.conversation_id ? 'bg-white/5' : ''
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] font-bold text-sm overflow-hidden flex-shrink-0">
                                  {conv.other_participant?.profile_picture || conv.other_participant?.logo_url ? (
                                    <img src={conv.other_participant?.profile_picture || conv.other_participant?.logo_url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    (conv.other_participant?.name || '?').charAt(0)
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-white font-medium truncate">{conv.other_participant?.name || 'Unknown'}</p>
                                  <p className="text-white/50 text-xs truncate">{conv.last_message || 'No messages'}</p>
                                </div>
                                {conv.unread_count > 0 && (
                                  <Badge className="bg-[#00d4aa] text-[#0a0b0c] text-xs">
                                    {conv.unread_count}
                                  </Badge>
                                )}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Chat Area */}
                    <div className="lg:col-span-2 bg-[#131416] rounded-xl border border-white/10 flex flex-col">
                      {(() => {
                        // Use ref if state is null (handles race condition)
                        const currentConv = activeConversation || activeConvRef.current;
                        if (!currentConv) {
                          return (
                            <div className="flex-1 flex items-center justify-center text-white/50">
                              Select a conversation or start a new one from the Players tab
                            </div>
                          );
                        }
                        return (
                        <>
                          {/* Chat Header */}
                          <div className="p-4 border-b border-white/10 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] font-bold overflow-hidden">
                              {currentConv.other_participant?.profile_picture || currentConv.other_participant?.logo_url ? (
                                <img src={currentConv.other_participant?.profile_picture || currentConv.other_participant?.logo_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                (currentConv.other_participant?.name || '?').charAt(0)
                              )}
                            </div>
                            <div>
                              <p className="text-white font-bold">{currentConv.other_participant?.name || 'Unknown'}</p>
                              <p className="text-white/50 text-xs">{currentConv.participant_type === 'team' ? 'Team' : 'Player'}</p>
                            </div>
                          </div>

                          {/* Messages */}
                          <div className="flex-1 p-4 overflow-y-auto space-y-4 max-h-[400px]">
                            {conversationMessages.length === 0 ? (
                              <div className="text-center text-white/50 text-sm py-8">
                                Start the conversation!
                              </div>
                            ) : (
                              conversationMessages.map(msg => (
                                <div
                                  key={msg.message_id}
                                  className={`flex ${msg.sender_id === currentPlayer.player_id ? 'justify-end' : 'justify-start'}`}
                                >
                                  <div
                                    className={`max-w-[70%] px-4 py-2 rounded-xl ${
                                      msg.sender_id === currentPlayer.player_id
                                        ? 'bg-[#00d4aa] text-[#0a0b0c]'
                                        : 'bg-white/10 text-white'
                                    }`}
                                  >
                                    <p className="text-sm">{msg.content}</p>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>

                          {/* Message Input */}
                          <div className="p-4 border-t border-white/10">
                            <div className="flex gap-3">
                              <Input
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Type a message..."
                                className="flex-1 bg-[#1a1c1f] border-white/10 text-white"
                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                              />
                              <Button
                                onClick={handleSendMessage}
                                disabled={!newMessage.trim() || messageSending}
                                className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c]"
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Privacy Settings Modal */}
      {showPrivacyModal && currentPlayer && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131416] border border-white/10 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Privacy Settings</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowPrivacyModal(false);
                  setPrivacyPassword('');
                }}
                className="text-white/60 hover:text-white"
              >
                ×
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-[#1a1b1e] rounded-lg">
                <div className={`p-3 rounded-full ${
                  currentPlayer.privacy === 'private' 
                    ? 'bg-yellow-500/20 text-yellow-400' 
                    : 'bg-[#00d4aa]/20 text-[#00d4aa]'
                }`}>
                  {currentPlayer.privacy === 'private' ? (
                    <Lock className="h-6 w-6" />
                  ) : (
                    <Globe className="h-6 w-6" />
                  )}
                </div>
                <div>
                  <p className="text-white font-semibold">
                    {currentPlayer.privacy === 'private' ? 'Private Profile' : 'Public Profile'}
                  </p>
                  <p className="text-white/60 text-sm">
                    {currentPlayer.privacy === 'private' 
                      ? 'Only your name and avatar are visible to others' 
                      : 'Your full profile is visible to everyone'}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-2">
                  Enter your profile password to change privacy:
                </label>
                <div className="relative">
                  <Input
                    type="password"
                    value={privacyPassword}
                    onChange={(e) => setPrivacyPassword(e.target.value)}
                    placeholder="Profile password"
                    className="bg-[#1a1b1e] border-white/10 text-white pr-10"
                    data-testid="privacy-password-input"
                  />
                </div>
              </div>

              <Button
                onClick={handlePrivacyToggle}
                disabled={!privacyPassword.trim() || privacyUpdating}
                className="w-full bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
                data-testid="confirm-privacy-btn"
              >
                {privacyUpdating ? (
                  <span className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-[#0a0b0c] border-t-transparent rounded-full animate-spin mr-2" />
                    Updating...
                  </span>
                ) : (
                  <>
                    {currentPlayer.privacy === 'private' ? (
                      <><Globe className="h-4 w-4 mr-2" />Make Profile Public</>
                    ) : (
                      <><Lock className="h-4 w-4 mr-2" />Make Profile Private</>
                    )}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        redirectPath="/community"
      />
    </div>
  );
};

export default CommunityHub;
